import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { BulkRemoveMember } from "@components/table/members/bulk-actions/bulk-remove-member";
import { BulkRemoveMemberDialog } from "@components/table/members/bulk-actions/bulk-remove-member-dialog";
import { BaseCompanyIdProps, BaseTeamIdProps } from "@custom-types/base-types";
import { MenuItem } from "@mui/material";
import { Text, DeleteIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useState } from "react";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { RemoveOption } from "@components/table/members/bulk-actions/bulk-remove-member-types";

interface Props extends BaseCompanyIdProps, BaseTeamIdProps {
  /** Available remove options to show in the dropdown */
  availableOptions: RemoveOption[];
}

/**
 * Dropdown button for bulk remove member actions.
 * Shows a single "Remove" button that opens a dropdown with multiple remove options
 * (e.g., "Remove from team", "Remove from workspace")
 */
export function BulkRemoveMemberDropdown({
  companyId,
  teamId,
  availableOptions,
}: Props): JSX.Element {
  const mode = useBulkActionsMode();
  const [selectedOption, setSelectedOption] = useState<RemoveOption | null>(
    null
  );

  // If only one option is available, render the single button directly
  if (availableOptions.length === 1) {
    const option = availableOptions[0];
    return (
      <BulkRemoveMember
        subjectType={option.subjectType}
        companyId={companyId}
        teamId={teamId}
        title={option.label}
      />
    );
  }

  function handleOptionClick(option: RemoveOption): void {
    setSelectedOption(option);
  }

  function handleDialogClose(): void {
    setSelectedOption(null);
  }

  const config = getBulkActionButtonConfig(mode, "Remove members");

  return (
    <>
      <FaroTextMenuButton
        buttonTitle={
          config.shouldShowText ? (
            <Text variant="buttonM">Remove</Text>
          ) : undefined
        }
        tooltipText={config.tooltipText}
        menuPosition={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
        }}
        rippleColor={config.rippleColor}
        shouldShowEndIcon={false}
        buttonSx={() => config.sx}
        startIcon={<DeleteIcon sx={{ width: "18px", height: "18px" }} />}
      >
        {availableOptions.map((option) => (
          <MenuItem key={option.key} onClick={() => handleOptionClick(option)}>
            <Text variant="bodyM">{option.label}</Text>
          </MenuItem>
        ))}
      </FaroTextMenuButton>

      {selectedOption && (
        <BulkRemoveMemberDialog
          subjectType={selectedOption.subjectType}
          companyId={companyId}
          teamId={teamId}
          isOpen={true}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
}
